import React, { useMemo, useState } from 'react';
import { formatPlacementLabel } from '../../lib/chartFormat';

const angleDescriptions = {
  ASC: 'ASC – the sign rising on the eastern horizon at birth.',
  MC: 'MC – the highest point of the chart, linked to direction and public life.',
  DSC: 'DSC – the point opposite the Ascendant, describing how we meet others.',
  IC: 'IC – the lowest point of the chart, linked to roots, home, and foundations.',
};

const ChartWheel = ({ chart, selectedKey, selectedHouse, selectedAspectId, selectedAngleKey, dominantHouse, dominantSignIndex, hoveredSector, reportMode, displayMode }) => {
  const [emphasisHovered, setEmphasisHovered] = useState(false);
  const wheelData = useMemo(() => {
    if (!chart) return null;
    return createWheelData(chart, {
      selectedKey,
      selectedHouse,
      selectedAspectId,
      selectedAngleKey,
      dominantHouse,
      dominantSignIndex,
      hoveredSector,
      displayMode,
    });
  }, [chart, selectedKey, selectedHouse, selectedAspectId, selectedAngleKey, dominantHouse, dominantSignIndex, hoveredSector, displayMode]);

  if (!wheelData) return null;

  return (
    <section className={`card${reportMode ? ' chart-wheel-report-mode' : ''}${displayMode ? ' chart-wheel-display-mode' : ''}`}>
      {!reportMode && <h2>Natal chart wheel</h2>}
      {!reportMode && <p className="chart-help">Full wheel with signs, houses, planets, aspects, and chart angles.</p>}
      {!reportMode && wheelData.chartEmphasis && !hoveredSector && (
        <div
          className={`chart-emphasis-badge${emphasisHovered ? ' chart-emphasis-badge-hovered' : ''}`}
          onMouseEnter={() => setEmphasisHovered(true)}
          onMouseLeave={() => setEmphasisHovered(false)}
          role="button"
          tabIndex={0}
          onClick={() => setEmphasisHovered((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEmphasisHovered((prev) => !prev);
            }
          }}
          aria-label={`Chart emphasis: ${wheelData.chartEmphasis.label}. ${wheelData.chartEmphasis.count} placements here. Click to highlight on wheel.`}
        >
          <span className="chart-emphasis-badge-heading">Chart emphasis</span>
          <span className="chart-emphasis-badge-value">{wheelData.chartEmphasis.label}</span>
          <span className="chart-emphasis-badge-count">{wheelData.chartEmphasis.count} placements here</span>
        </div>
      )}
      <div className="chart-wheel-host" aria-label="Natal chart wheel">
        <svg className="chart-wheel-svg" viewBox="0 0 900 900" role="img" aria-label="Natal chart wheel">
          <defs>
            <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.08" />
            </filter>
          </defs>
          <circle cx="450" cy="450" r="418" fill="#fff8ed" stroke="#d0bea1" strokeWidth="2" />
          <circle cx="450" cy="450" r="352" fill="none" stroke="#3a2f23" strokeWidth="1.5" />
          {!displayMode && wheelData.densityRingArcs?.map((arc, i) => (
            <path
              key={`density-${i}`}
              d={arc.path}
              className={`wheel-density-arc wheel-density-${arc.count >= 4 ? '4' : arc.count >= 3 ? '3' : '2'}`}
              aria-hidden="true"
            />
          ))}
          {!displayMode && wheelData.emphasisSectorPath && (
            <path
              d={wheelData.emphasisSectorPath}
              className={`wheel-emphasis-sector${(emphasisHovered || hoveredSector) ? ' wheel-emphasis-sector-hovered' : ''}`}
              aria-hidden="true"
            />
          )}
          {!displayMode && wheelData.dominantSignArc && <path d={wheelData.dominantSignArc} className="wheel-dominant-sign-arc" />}
          <circle cx="450" cy="450" r="286" fill="none" stroke="#3a2f23" strokeWidth="1.5" />
          <circle cx="450" cy="450" r="214" fill="none" stroke="#b4a183" strokeWidth="1.2" />

          {wheelData.signBoundaries.map((deg) => {
            const p1 = toPoint(450, 450, 418, deg);
            const p2 = toPoint(450, 450, 352, deg);
            return <line key={`sign-${deg}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} className="wheel-sign-line" />;
          })}

          {wheelData.signSegments.map((segment) => (
            <path key={`sign-segment-${segment.name}`} d={segment.arcPath} className="wheel-sign-segment-hit">
              <title>{segment.name} sign segment</title>
            </path>
          ))}

          {wheelData.signLabels.map((label) => (
            <text key={`sign-label-${label.deg}`} x={label.x} y={label.y} className="wheel-sign-label">
              {label.glyph}
            </text>
          ))}

          {wheelData.houseLines.map((line) => (
            <g key={`house-${line.house}`}>
              <path d={line.hoverPath} className="wheel-house-sector-hit">
                <title>House {line.house} sector in {line.sign}</title>
              </path>
              <path d={line.arcPath} className={`wheel-house-arc${line.clustered ? ' wheel-house-arc-cluster' : ''}${line.highlighted ? ' wheel-house-arc-selected' : ''}${line.dimmed ? ' wheel-house-arc-dimmed' : ''}`} />
              <line x1={line.outer.x} y1={line.outer.y} x2={line.inner.x} y2={line.inner.y} className={`wheel-house-line${line.boundaryHighlighted ? ' wheel-house-line-selected' : ''}${line.dimmed ? ' wheel-house-line-dimmed' : ''}`} />
              <text x={line.label.x} y={line.label.y} className="wheel-house-label">{line.house}</text>
            </g>
          ))}

          {wheelData.aspectLines.map((line, index) => (
            <line
              key={`aspect-${index}`}
              x1={line.a.x}
              y1={line.a.y}
              x2={line.b.x}
              y2={line.b.y}
              className={`wheel-aspect-line wheel-aspect-${line.type}${line.highlighted ? ' wheel-aspect-selected' : ''}${line.dimmed ? ' wheel-aspect-dimmed' : ''}`}
            />
          ))}

          {wheelData.planets.map((planet) => (
            <g key={`planet-${planet.key}`} filter="url(#softShadow)">
              <circle cx={planet.x} cy={planet.y} r="16" className={`wheel-planet-dot${planet.clustered ? ' wheel-planet-dot-cluster' : ''}${planet.highlighted ? ' wheel-planet-dot-selected' : ''}${planet.dimmed ? ' wheel-planet-dot-dimmed' : ''}`} />
              <text x={planet.x} y={planet.y + 5} className={`wheel-planet-glyph${planet.highlighted ? ' wheel-planet-glyph-selected' : ''}${planet.dimmed ? ' wheel-planet-glyph-dimmed' : ''}`}>{planet.glyph}</text>
              <text x={planet.degree.x} y={planet.degree.y} className="wheel-planet-degree">{planet.degree.text}</text>
              <title>{planet.name || planet.nameFi || planet.key} in {planet.sign}{Number.isFinite(Number(planet.house)) ? `, House ${planet.house}` : ''}</title>
            </g>
          ))}

          {wheelData.angles.map((angle) => (
            <g key={`angle-${angle.label}`}>
              <line x1={angle.inner.x} y1={angle.inner.y} x2={angle.outer.x} y2={angle.outer.y} className={`wheel-angle-line${angle.highlighted ? ' wheel-angle-line-selected' : ''}`} />
              <text
                x={angle.labelPoint.x}
                y={angle.labelPoint.y}
                className={`wheel-angle-label${angle.highlighted ? ' wheel-angle-label-selected' : ''}`}
                aria-label={angleDescriptions[angle.label] || angle.label}
              >
                {angle.label}
                <title>{angleDescriptions[angle.label] || angle.label}</title>
              </text>
            </g>
          ))}
        </svg>
      </div>
      {!reportMode && wheelData.selectedPlacementLabel && (
        <div className="chart-selected-placement-badge chart-wheel-placement-badge">
          <span className="chart-selected-badge-label">Chart highlight</span>
          <span className="chart-selected-badge-value">{wheelData.selectedPlacementLabel}</span>
        </div>
      )}
      {!reportMode && (
      <div className="chart-wheel-legend">
        <h4>Chart key</h4>
        <p><span className="legend-swatch legend-sign" /> Signs — outer symbolic framework</p>
        <p><span className="legend-swatch legend-house" /> Houses — life areas</p>
        <p><span className="legend-swatch legend-planet" /> Planets — active functions</p>
        <p><span className="legend-swatch legend-flow" /> Blue lines — flowing aspects</p>
        <p><span className="legend-swatch legend-tense" /> Red lines — tension aspects</p>
        <p><span className="legend-swatch legend-angle" /> Gold lines — chart angles</p>
      </div>
      )}
    </section>
  );
};

const signGlyphs = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const signNames = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
const planetGlyphs = {
  sun: '☉',
  moon: '☽',
  mercury: '☿',
  venus: '♀',
  mars: '♂',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
  northnode: '☊',
  lilith: '⚸',
  chiron: '⚷',
};

function createWheelData(chart, selection) {
  const {
    selectedKey,
    selectedHouse,
    selectedAspectId,
    selectedAngleKey,
    dominantHouse,
    dominantSignIndex,
    hoveredSector,
    displayMode,
  } = selection;
  const signBoundaries = Array.from({ length: 12 }, (_, i) => i * 30);
  const signSegments = signBoundaries.map((deg, index) => ({
    name: signNames[index],
    arcPath: arcBetween(450, 450, 386, deg, deg + 30),
  }));
  const signLabels = signBoundaries.map((deg, index) => {
    const p = toPoint(450, 450, 385, deg + 15);
    return { deg, glyph: signGlyphs[index], x: p.x, y: p.y + 6 };
  });

  const normalizedHouses = [...chart.houses].sort((a, b) => Number(a.house) - Number(b.house));
  const selectedPlanet = chart.planets.find((planet) => planet.key === selectedKey);
  const selectedAspect = chart.aspects.find((aspect) => aspect.id === selectedAspectId);
  const selectedHouseValue = Number.isFinite(Number(selectedHouse)) ? Number(selectedHouse) : Number(selectedPlanet?.house);
  const selectedPlanetKeys = new Set(selectedPlanet ? [selectedPlanet.key] : []);
  if (selectedAspect) {
    selectedPlanetKeys.add(selectedAspect.p1);
    selectedPlanetKeys.add(selectedAspect.p2);
  }
  if (Number.isFinite(selectedHouseValue)) {
    chart.planets.filter((planet) => Number(planet.house) === selectedHouseValue).forEach((planet) => selectedPlanetKeys.add(planet.key));
  }

  const houseLines = normalizedHouses.map((house, index) => {
    const deg = normalize(house.lon);
    const next = normalizedHouses[(index + 1) % normalizedHouses.length];
    const nextDeg = normalize(next.lon);
    const span = nextDeg >= deg ? nextDeg - deg : 360 - deg + nextDeg;
    const center = normalize(deg + span / 2);
    return {
      house: house.house,
      outer: toPoint(450, 450, 286, deg),
      inner: toPoint(450, 450, 214, deg),
      label: toPoint(450, 450, 248, center),
      arcPath: arcBetween(450, 450, 286, deg, nextDeg),
      hoverPath: arcBetween(450, 450, 250, deg, nextDeg),
      highlighted: Number(selectedHouseValue) === Number(house.house),
      clustered: Number(dominantHouse) === Number(house.house),
      dimmed: Number.isFinite(selectedHouseValue) && Number(selectedHouseValue) !== Number(house.house),
      boundaryHighlighted:
        Number(selectedHouseValue) === Number(house.house) ||
        Number(selectedHouseValue) === Number(house.house === 12 ? 1 : house.house + 1),
      sign: house.sign,
    };
  });

  const planets = offsetPlanets(chart.planets).map((planet) => {
    const p = toPoint(450, 450, 320 - planet.offset, planet.lon);
    const d = toPoint(450, 450, 347 - planet.offset, planet.lon);
    const degInSign = normalize(planet.lon) % 30;
    const deg = Math.floor(degInSign);
    const min = Math.floor((degInSign - deg) * 60);
    return {
      ...planet,
      highlighted: selectedPlanetKeys.has(planet.key),
      dimmed: selectedPlanetKeys.size > 0 && !selectedPlanetKeys.has(planet.key),
      x: p.x,
      y: p.y,
      degree: {
        x: d.x,
        y: d.y,
        text: `${deg}°${String(min).padStart(2, '0')}′`,
      },
    };
  });

  const lonByKey = new Map(chart.planets.map((p) => [String(p.key).toLowerCase(), normalize(p.lon)]));
  const allAspects = chart.aspects
    .map((a) => {
      const lonA = lonByKey.get(String(a.p1).toLowerCase());
      const lonB = lonByKey.get(String(a.p2).toLowerCase());
      if (lonA == null || lonB == null) return null;
      return {
        id: a.id,
        type: String(a.type || 'conjunction').toLowerCase(),
        a: toPoint(450, 450, 198, lonA),
        b: toPoint(450, 450, 198, lonB),
        highlighted: a.id === selectedAspectId,
        dimmed: selectedAspectId && a.id !== selectedAspectId,
      };
    })
    .filter(Boolean);
  const aspectLines = displayMode === true ? [] : allAspects;

  const angleDefs = [
    { label: 'ASC', lon: chart.angles.ascLon },
    { label: 'DSC', lon: chart.angles.dcLon },
    { label: 'MC', lon: chart.angles.mcLon },
    { label: 'IC', lon: chart.angles.icLon },
  ].filter((angle) => Number.isFinite(Number(angle.lon)));

  const angles = angleDefs.map((angle) => ({
    label: angle.label,
    inner: toPoint(450, 450, 214, angle.lon),
    outer: toPoint(450, 450, 418, angle.lon),
    labelPoint: toPoint(450, 450, 438, angle.lon),
    highlighted: String(selectedAngleKey || '').toUpperCase() === angle.label,
  }));

  const dominantSignArc = displayMode
    ? null
    : (Number.isFinite(Number(dominantSignIndex))
        ? arcBetween(450, 450, 352, Number(dominantSignIndex) * 30, (Number(dominantSignIndex) + 1) * 30)
        : null);
  const clusteredPlanetKeys = displayMode ? new Set() : detectClusteredPlanets(chart.planets);
  const chartEmphasis = computeChartEmphasis(chart);
  const emphasisForDisplay = displayMode ? null : (hoveredSector ? buildEmphasisFromHover(chart, hoveredSector) : chartEmphasis);
  const emphasisSectorPath = displayMode ? null : (emphasisForDisplay ? buildEmphasisSectorPath(chart, emphasisForDisplay) : null);
  const densityRingArcs = displayMode ? [] : detectDensityClusters(chart.planets);
  const selectedPlacementLabel = selectedPlanet ? formatPlacementLabel(selectedPlanet) : null;

  return {
    signBoundaries,
    signSegments,
    signLabels,
    houseLines,
    planets: planets.map((planet) => ({
      ...planet,
      clustered: clusteredPlanetKeys.has(planet.key),
      glyph: planetGlyphs[String(planet.key).toLowerCase()] || String(planet.key).slice(0, 2).toUpperCase(),
    })),
    aspectLines,
    angles,
    dominantSignArc,
    chartEmphasis,
    emphasisSectorPath,
    densityRingArcs,
    selectedPlacementLabel,
  };
}

function buildEmphasisFromHover(chart, hoveredSector) {
  if (!hoveredSector) return null;
  const { type, value } = hoveredSector;
  if (type === 'sign') {
    const signIdx = signNames.indexOf(value);
    if (signIdx >= 0) {
      return { sign: value, signIndex: signIdx, house: null };
    }
  }
  if (type === 'house' && Number.isFinite(Number(value))) {
    return { sign: null, signIndex: -1, house: Number(value) };
  }
  return null;
}

function detectDensityClusters(planets) {
  if (!planets?.length) return [];
  const ARC_DEG = 40;
  const sorted = planets
    .map((p) => ({ key: p.key, lon: normalize(p.lon) }))
    .sort((a, b) => a.lon - b.lon);
  const duplicated = [...sorted, ...sorted.map((p) => ({ ...p, lon: p.lon + 360 }))];
  const seen = new Set();
  const clusters = [];
  for (let start = 0; start < duplicated.length; start++) {
    const startLon = duplicated[start].lon;
    const windowItems = duplicated.filter((p) => p.lon >= startLon && p.lon <= startLon + ARC_DEG);
    const unique = [...new Map(windowItems.map((p) => [p.key, p])).values()];
    if (unique.length >= 2) {
      const keys = unique.map((p) => p.key).sort().join(',');
      if (seen.has(keys)) continue;
      seen.add(keys);
      const lons = unique.map((p) => p.lon % 360);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      const pad = Math.min(6, (ARC_DEG - (maxLon - minLon)) / 2);
      clusters.push({
        startLon: Math.max(0, minLon - pad),
        endLon: Math.min(360, maxLon + pad),
        count: unique.length,
      });
    }
  }
  clusters.sort((a, b) => b.count - a.count);
  const cx = 450;
  const cy = 450;
  const r = 200;
  return clusters.slice(0, 3).map((c) => ({
    path: arcBetween(cx, cy, r, c.startLon, c.endLon),
    count: c.count,
  }));
}

function computeChartEmphasis(chart) {
  if (!chart?.planets?.length) return null;
  const signCounts = {};
  const houseCounts = {};
  chart.planets.forEach((p) => {
    if (p.sign) signCounts[p.sign] = (signCounts[p.sign] || 0) + 1;
    const h = Number(p.house);
    if (Number.isFinite(h)) houseCounts[h] = (houseCounts[h] || 0) + 1;
  });
  const signEntries = Object.entries(signCounts).filter(([, c]) => c >= 3);
  const houseEntries = Object.entries(houseCounts).filter(([, c]) => c >= 3);
  const bestSign = signEntries.sort((a, b) => b[1] - a[1])[0];
  const bestHouse = houseEntries.sort((a, b) => b[1] - a[1])[0];
  if (!bestSign && !bestHouse) return null;
  const signName = bestSign?.[0];
  const signCount = bestSign?.[1] || 0;
  const houseNum = bestHouse ? Number(bestHouse[0]) : null;
  const houseCount = bestHouse?.[1] || 0;
  const count = Math.max(signCount, houseCount);
  const labelParts = [];
  if (signName) labelParts.push(signName);
  if (houseNum) labelParts.push(`${ordinalHouse(houseNum)} House`);
  return {
    sign: signName,
    house: houseNum,
    count,
    signIndex: signName ? signNames.indexOf(signName) : -1,
    label: labelParts.join(' — '),
  };
}

function buildEmphasisSectorPath(chart, emphasis) {
  const cx = 450;
  const cy = 450;
  const rInner = 218;
  const rOuter = 282;
  let startLon;
  let endLon;
  if (Number.isFinite(emphasis.house) && chart.houses?.length) {
    const normalizedHouses = [...chart.houses].sort((a, b) => Number(a.house) - Number(b.house));
    const house = normalizedHouses.find((h) => Number(h.house) === emphasis.house);
    const idx = normalizedHouses.findIndex((h) => Number(h.house) === emphasis.house);
    if (house && idx >= 0) {
      const next = normalizedHouses[(idx + 1) % normalizedHouses.length];
      startLon = normalize(house.lon);
      endLon = normalize(next.lon);
    }
  }
  if (startLon == null) {
    const signIdx = emphasis.signIndex >= 0 ? emphasis.signIndex : signNames.indexOf(emphasis.sign);
    if (signIdx >= 0) {
      startLon = signIdx * 30;
      endLon = startLon + 30;
    }
  }
  if (startLon == null || endLon == null) return null;
  return sectorPath(cx, cy, rInner, rOuter, startLon, endLon);
}

function sectorPath(cx, cy, rInner, rOuter, startLon, endLon) {
  const startOuter = toPoint(cx, cy, rOuter, startLon);
  const endOuter = toPoint(cx, cy, rOuter, endLon);
  const startInner = toPoint(cx, cy, rInner, startLon);
  const endInner = toPoint(cx, cy, rInner, endLon);
  const span = ((normalize(endLon) - normalize(startLon)) + 360) % 360;
  const largeArc = span > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${startOuter.x} ${startOuter.y} A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y} L ${endInner.x} ${endInner.y} A ${rInner} ${rInner} 0 ${largeArc} 0 ${startInner.x} ${startInner.y} Z`;
}

function normalize(value) {
  return ((Number(value) % 360) + 360) % 360;
}

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function toPoint(cx, cy, radius, longitude) {
  const rad = ((normalize(longitude) - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function arcBetween(cx, cy, radius, startLon, endLon) {
  const start = toPoint(cx, cy, radius, startLon);
  const end = toPoint(cx, cy, radius, endLon);
  const span = ((normalize(endLon) - normalize(startLon)) + 360) % 360;
  const largeArc = span > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function offsetPlanets(planets) {
  const buckets = new Map();
  return planets.map((planet) => {
    const bucket = Math.round(normalize(planet.lon) / 5);
    const count = buckets.get(bucket) || 0;
    buckets.set(bucket, count + 1);
    return { ...planet, offset: count * 18 };
  });
}

function detectClusteredPlanets(planets) {
  const clustered = new Set();
  if (!Array.isArray(planets) || planets.length < 3) return clustered;
  const sorted = planets
    .map((planet, index) => ({ key: planet.key, lon: normalize(planet.lon), index }))
    .sort((a, b) => a.lon - b.lon);
  const duplicated = [...sorted, ...sorted.map((item) => ({ ...item, lon: item.lon + 360 }))];
  let best = [];
  let start = 0;
  for (let end = 0; end < duplicated.length; end++) {
    while (duplicated[end].lon - duplicated[start].lon > 60) {
      start++;
    }
    const windowItems = duplicated.slice(start, end + 1);
    const keys = [...new Set(windowItems.map((item) => item.key))];
    if (keys.length > best.length) {
      best = keys;
    }
  }
  if (best.length >= 3) {
    best.forEach((key) => clustered.add(key));
  }
  return clustered;
}

export default ChartWheel;
